import { useCallback, useEffect, useRef, useState } from "react";

interface WebRTCAudioManagerProps {
  onAudioReceived?: (audioData: ArrayBuffer) => void;
  onStreamEvent?: (eventData: any) => void;
  isListening?: boolean;
  isThinking?: boolean;
  isSpeaking?: boolean;
  onConnectError?: () => void;
  onConnectionStart?: () => void;
  onConnectionComplete?: () => void;
  language?: string;
}

export const useWebRTCAudioManager = ({
  onStreamEvent,
  onConnectError,
  onConnectionStart,
  onConnectionComplete,
  language = "vi",
}: WebRTCAudioManagerProps) => {
  // Refs
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const clientIdRef = useRef<string>("");

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [clientId, setClientId] = useState("");

  // Refs for callbacks to avoid stale closures
  const onStreamEventRef = useRef(onStreamEvent);
  const onConnectErrorRef = useRef(onConnectError);
  const onConnectionCompleteRef = useRef(onConnectionComplete);

  useEffect(() => {
    onStreamEventRef.current = onStreamEvent;
  }, [onStreamEvent]);

  useEffect(() => {
    onConnectErrorRef.current = onConnectError;
  }, [onConnectError]);

  useEffect(() => {
    onConnectionCompleteRef.current = onConnectionComplete;
  }, [onConnectionComplete]);

  // Get backend URL
  const getBackendUrl = useCallback(() => {
    return import.meta.env.VITE_BACKEND_URL;
  }, []);

  // Fetch TURN credentials from backend
  const fetchTurnCredentials = useCallback(async () => {
    try {
      const baseUrl = getBackendUrl();
      const response = await fetch(`${baseUrl}/v1/voice-bot/webrtc/turn-credentials`);

      if (!response.ok) {
        console.warn(`⚠️ [WebRTC] Failed to fetch TURN credentials: ${response.status}`);
        return null;
      }

      const data = await response.json();
      console.log(`✅ [WebRTC] Fetched TURN credentials from backend`);
      return data;
    } catch (error) {
      console.error("❌ [WebRTC] Error fetching TURN credentials:", error);
      return null;
    }
  }, [getBackendUrl]);

  // Generate client ID
  const generateClientId = useCallback(() => {
    const newId = Math.random().toString(36).substring(2, 18);
    clientIdRef.current = newId;
    setClientId(newId);
    return newId;
  }, []);

  // Get user media with optimal WebRTC settings
  const getUserMedia = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        // Browser built-in echo cancellation - CRITICAL for voice calls
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });
    return stream;
  }, []);

  // Cleanup resources
  const cleanup = useCallback(() => {
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }
  }, []);

  // Connect to WebRTC server
  const connect = useCallback(async () => {
    // Cleanup any existing connection first
    cleanup();

    const newClientId = generateClientId();
    onConnectionStart?.();
    console.log(`🔌 [WebRTC] Connecting with client_id=${newClientId}`);

    try {
      // Get user media with browser AEC
      const stream = await getUserMedia();
      localStreamRef.current = stream;

      // Fetch TURN credentials from backend (secure, dynamic credentials)
      console.log("🔄 [WebRTC] Fetching TURN credentials from backend...");
      const turnCredentials = await fetchTurnCredentials();

      // Build ICE servers configuration
      const iceServers: RTCIceServer[] = [
        // STUN servers (for direct connections)
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
      ];

      // Add TURN servers from backend
      if (turnCredentials) {
        console.log("✅ [WebRTC] Using dynamic TURN credentials from Metered.ca");
        console.log(`🔄 [WebRTC] TURN servers:`, turnCredentials.urls);

        // Add each TURN server URL
        turnCredentials.urls.forEach((url: string) => {
          iceServers.push({
            urls: url,
            username: turnCredentials.username,
            credential: turnCredentials.credential,
          });
        });
      } else {
        console.warn("⚠️ [WebRTC] Using fallback public TURN servers");
        // Fallback to free public TURN servers
        iceServers.push(
          {
            urls: "turn:openrelay.metered.ca:80",
            username: "openrelayproject",
            credential: "openrelayproject",
          }
        );
      }

      const config: RTCConfiguration = {
        iceServers,
        iceCandidatePoolSize: 10, // Pre-gather candidates for faster connection
      };

      const pc = new RTCPeerConnection(config);
      peerConnectionRef.current = pc;

      // ICE candidates - collect before answer, send after
      const pendingIceCandidates: RTCIceCandidate[] = [];
      let canSendIceCandidates = false;

      const sendIceCandidate = async (candidate: RTCIceCandidate) => {
        try {
          const baseUrl = getBackendUrl();
          const response = await fetch(`${baseUrl}/v1/voice-bot/webrtc/ice`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              client_id: clientIdRef.current,
              candidate: candidate.candidate,
              sdpMid: candidate.sdpMid,
              sdpMLineIndex: candidate.sdpMLineIndex,
            }),
          });
          if (!response.ok) {
            console.warn(`[WebRTC] ICE candidate send failed: ${response.status}`);
          }
        } catch (error) {
          console.error("❌ [WebRTC] Error sending ICE candidate:", error);
        }
      };

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          // Log ICE candidate type for debugging TURN usage
          const candidateType = event.candidate.candidate.includes("typ relay") ? "🔄 TURN relay" :
                               event.candidate.candidate.includes("typ srflx") ? "🌐 STUN srflx" :
                               event.candidate.candidate.includes("typ host") ? "🏠 host" : "❓ unknown";
          console.log(`🧊 [WebRTC] ICE candidate: ${candidateType}`);

          if (canSendIceCandidates) {
            sendIceCandidate(event.candidate);
          } else {
            pendingIceCandidates.push(event.candidate);
          }
        }
      };

      // Monitor ICE connection state for early failure detection
      pc.oniceconnectionstatechange = () => {
        console.log(`🧊 [WebRTC] ICE state: ${pc.iceConnectionState}`);
        if (pc.iceConnectionState === "failed") {
          console.error("❌ [WebRTC] ICE connection failed");
          setIsConnected(false);
          onConnectErrorRef.current?.();
        }
      };

      // Monitor connection state
      pc.onconnectionstatechange = () => {
        console.log(`🔗 [WebRTC] Connection state: ${pc.connectionState}`);
        if (pc.connectionState === "connected") {
          setIsConnected(true);
          onConnectionCompleteRef.current?.();
        } else if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
          setIsConnected(false);
          onConnectErrorRef.current?.();
        }
      };

      // Handle incoming audio track
      pc.ontrack = (event) => {
        console.log(`🎵 [WebRTC] Received track: ${event.track.kind}`);
        if (event.track.kind === "audio" && event.streams[0]) {
          // Reuse audio element if exists
          let audio = audioElementRef.current;
          if (!audio) {
            audio = new Audio();
            audioElementRef.current = audio;
          }

          audio.srcObject = event.streams[0];
          audio.autoplay = true;
          audio.volume = 1.0;

          audio.play().catch((e) => {
            console.warn("🔇 [WebRTC] Audio autoplay blocked:", e);
          });
        }
      };

      // Handle data channel from server
      pc.ondatachannel = (event) => {
        console.log(`📡 [WebRTC] Data channel received from server: ${event.channel.label}`);
        const channel = event.channel;

        channel.onmessage = (msgEvent) => {
          try {
            const data = JSON.parse(msgEvent.data);
            // Log all events from server data channel (purple for stop_audio)
            if (data.event_type === "stop_audio") {
              console.log("%c🛑 [WebRTC Server DC] RECEIVED stop_audio event!", "color: magenta; font-weight: bold", data);
            } else {
              console.log("📨 [WebRTC Server DC] Event received:", data.event_type, data);
            }
            onStreamEventRef.current?.(data);
          } catch (error) {
            console.error("❌ [WebRTC] Error parsing message:", error);
          }
        };

        channel.onerror = (error) => {
          console.error("❌ [WebRTC] Data channel error:", error);
        };

        // Update ref if this is a newer channel
        dataChannelRef.current = channel;
      };

      // Add audio track to peer connection
      stream.getAudioTracks().forEach((track) => {
        pc.addTrack(track, stream);
        console.log(`🎤 [WebRTC] Added audio track: ${track.label}`);
      });

      // Create data channel for client->server events
      const dataChannel = pc.createDataChannel("events", { ordered: true });
      dataChannelRef.current = dataChannel;

      dataChannel.onopen = () => {
        console.log("✅ [WebRTC] Data channel opened");
      };

      dataChannel.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Log all events from data channel (purple for stop_audio)
          if (data.event_type === "stop_audio") {
            console.log("%c🛑 [WebRTC DC] RECEIVED stop_audio event!", "color: magenta; font-weight: bold", data);
          } else {
            console.log("📨 [WebRTC DC] Event received:", data.event_type, data);
          }
          onStreamEventRef.current?.(data);
        } catch (error) {
          console.error("❌ [WebRTC] Error parsing message:", error);
        }
      };

      dataChannel.onerror = (error) => {
        console.error("❌ [WebRTC] Data channel error:", error);
      };

      // Create offer
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      });
      await pc.setLocalDescription(offer);

      // Send offer to server
      const baseUrl = getBackendUrl();
      console.log(`📤 [WebRTC] Sending offer to server`);

      const response = await fetch(`${baseUrl}/v1/voice-bot/webrtc/offer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sdp: offer.sdp,
          type: offer.type,
          client_id: newClientId,
          language: "km", // Khmer-only deployment — cached audio + STT are Khmer
        }),
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const answer = await response.json();
      console.log(`📥 [WebRTC] Received answer`);

      await pc.setRemoteDescription(new RTCSessionDescription({
        sdp: answer.sdp,
        type: answer.type,
      }));

      // Now send pending ICE candidates
      canSendIceCandidates = true;
      for (const candidate of pendingIceCandidates) {
        sendIceCandidate(candidate);
      }

      // Wait for data channel to open (with timeout)
      await new Promise<void>((resolve) => {
        if (dataChannel.readyState === "open") {
          resolve();
          return;
        }

        const timeout = setTimeout(() => {
          console.warn("⚠️ [WebRTC] Data channel open timeout, continuing anyway");
          resolve();
        }, 5000);

        dataChannel.onopen = () => {
          clearTimeout(timeout);
          console.log("✅ [WebRTC] Data channel opened");
          resolve();
        };
      });

      console.log(`✅ [WebRTC] Connected`);
    } catch (error) {
      console.error("❌ [WebRTC] Connection error:", error);
      cleanup();
      onConnectErrorRef.current?.();
      throw error;
    }
  }, [cleanup, generateClientId, getUserMedia, getBackendUrl, language, onConnectionStart]);

  // Disconnect
  const disconnect = useCallback(async () => {
    console.log("🔌 [WebRTC] Disconnecting...");

    const currentClientId = clientIdRef.current;

    cleanup();

    // Notify server
    if (currentClientId) {
      try {
        const baseUrl = getBackendUrl();
        await fetch(`${baseUrl}/v1/voice-bot/webrtc/close/${currentClientId}`, {
          method: "DELETE",
        });
      } catch (error) {
        console.error("❌ [WebRTC] Error notifying server:", error);
      }
    }

    clientIdRef.current = "";
    setClientId("");
    setIsConnected(false);
    console.log("✅ [WebRTC] Disconnected");
  }, [cleanup, getBackendUrl]);

  // Send event through data channel
  const sendEvent = useCallback((eventType: string, data?: any) => {
    const channel = dataChannelRef.current;
    if (channel?.readyState === "open") {
      channel.send(JSON.stringify({
        event_type: eventType,
        data: data || {},
      }));
    }
  }, []);

  // Audio control
  const muteAudio = useCallback(() => {
    if (audioElementRef.current) {
      audioElementRef.current.volume = 0;
    }
  }, []);

  const unmuteAudio = useCallback(() => {
    if (audioElementRef.current) {
      audioElementRef.current.volume = 1.0;
    }
  }, []);

  const stopAudio = useCallback(() => {
    muteAudio();
  }, [muteAudio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Use cleanup ref to avoid stale closure
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.srcObject = null;
      }
    };
  }, []);

  // Placeholder functions for compatibility
  const updateListeningState = useCallback((_listening?: boolean) => {}, []);
  const updateThinkingState = useCallback((_thinking?: boolean) => {}, []);
  const stopAudioProcessing = useCallback(() => {}, []);
  const updatePlaybackStream = useCallback((_stream?: MediaStream | null) => {}, []);
  const stopVAD = useCallback(() => {}, []);

  return {
    connect,
    disconnect,
    sendEvent,
    stopAudioProcessing,
    updatePlaybackStream,
    updateListeningState,
    updateThinkingState,
    muteAudio,
    unmuteAudio,
    stopAudio,
    isConnected,
    clientId,
    isUserSpeaking: false,
    stopVAD,
  };
};
